import { useState } from "react";
import type { AuthContextType } from "@/features/auth/context/AuthContext";
import { useAuth } from "@/features/auth/hooks/useAuth";

export type LoginOnboardingTrail = {
  isSecurityRequired: boolean
  isSessionLoading: boolean
  hasSecurityStep: boolean
  hasLegalSteps: boolean
  isOnboardingActive: boolean
};

type AuthStepFlags = {
  isSecurityRequired: boolean
  isLegalComplianceRequired: boolean
  isSessionLoading: boolean
  isBackToLogin: boolean
};

const readAuthStepFlags = (auth: AuthContextType): AuthStepFlags => {
  const isSecurityRequired = auth.isTwoFactorRequired || auth.isCellPhoneRequired;
  const isSessionLoading = auth.isWaitingForUser || auth.isWaitingForOrganization || auth.isWaitingForPlan || auth.isWaitingForCmsPages;
  const isBackToLogin = !isSecurityRequired && !auth.isLegalComplianceRequired && !isSessionLoading && !auth.isAuthenticated;

  return {
    isSecurityRequired, isLegalComplianceRequired: auth.isLegalComplianceRequired, isSessionLoading, isBackToLogin,
  };
};

// Remembers which onboarding steps this login went through, so the progress
// keeps them once their auth flags are reset, and the layout does not flash
// back to the login screen while the space loads after the recap.
export const useLoginOnboardingTrail = (): LoginOnboardingTrail => {
  const flags = readAuthStepFlags(useAuth());
  const [hasSecurityStep, setHasSecurityStep] = useState(false);
  const [hasLegalSteps, setHasLegalSteps] = useState(false);

  // State derived from the previous renders, adjusted during render.
  if (flags.isSecurityRequired && !hasSecurityStep) setHasSecurityStep(true);
  if (flags.isLegalComplianceRequired && !hasLegalSteps) setHasLegalSteps(true);
  if (flags.isBackToLogin && (hasSecurityStep || hasLegalSteps)) {
    setHasSecurityStep(false);
    setHasLegalSteps(false);
  }

  return {
    isSecurityRequired: flags.isSecurityRequired,
    isSessionLoading: flags.isSessionLoading,
    hasSecurityStep: hasSecurityStep || flags.isSecurityRequired,
    hasLegalSteps: hasLegalSteps || flags.isLegalComplianceRequired,
    isOnboardingActive: flags.isSecurityRequired || flags.isLegalComplianceRequired || (hasLegalSteps && !flags.isBackToLogin),
  };
};
